/** Datenzugriff über TanStack Query -- Cache, Wiederholung und Invalidierung. */

import { useEffect, useRef, useState } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { ApiError, api } from '../api/client'
import { vergissOrt } from '../lib/letzterChat'
import { aufnahmeKopf } from '../lib/images'
import type { Aufnahme } from '../lib/exif'
import type {
  Health,
  Highlight,
  HighlightsResponse,
  Invitation,
  LocateResponse,
  LookupResponse,
  PublishManyResponse,
  SnapResponse,
  InvitationPreview,
  InvitationStatus,
  JoinRequest,
  Me,
  Media,
  Member,
  Message,
  Person,
  MessagePage,
  Reaction,
  Reisefotos,
  RescanResponse,
  Settings,
  SettingsResponse,
  Thread,
  Track,
  TracksResponse,
  Trip,
  TripState,
  UmgebungResponse,
} from '../api/types'

export const keys = {
  me: ['me'] as const,
  threads: ['threads'] as const,
  people: ['people'] as const,
  trips: ['trips'] as const,
  trip: (id: number) => ['trip', id] as const,
  members: (id: number) => ['members', id] as const,
  invitations: (id: number) => ['invitations', id] as const,
  requests: (id: number) => ['requests', id] as const,
  invitationStatus: (code: string) => ['invitation-status', code] as const,
  settings: ['settings'] as const,
  messages: (tripId: number, withUser?: number) =>
    ['messages', tripId, withUser ?? null] as const,
  tracks: (tripId: number) => ['tracks', tripId] as const,
  highlights: (tripId: number) => ['highlights', tripId] as const,
  reisefotos: (tripId: number) => ['reisefotos', tripId] as const,
}

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<Me>('/api/auth/me'),
    // Eine fehlende Anmeldung ist kein Fehler, der sich durch Wiederholen
    // beheben ließe.
    retry: false,
    staleTime: 5 * 60_000,
  })
}

/**
 * Der Zustand der Installation -- nur gefragt, wenn etwas schiefgeht.
 *
 * Ausgeliefert wird per FTP, und das Schema zieht ein Mensch von Hand nach.
 * Bleibt dabei etwas liegen, ist die eigentliche Auskunft „es fehlt die
 * Spalte X" -- und die gehört vor die Nase dessen, der davorsteht, nicht in
 * ein Serverprotokoll.
 */
export function useHealth(enabled: boolean) {
  return useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<Health>('/api/health'),
    enabled,
    retry: false,
  })
}

/**
 * Die einstellbaren Zahlen.
 *
 * Braucht jeder, nicht nur die Verwaltung: die Bildgröße wirkt im Browser,
 * und der verkleinert, bevor irgendetwas zum Server geht. Deshalb lange
 * haltbar und mit Vorgaben unterlegt -- ein Foto darf nicht darauf warten
 * müssen, dass diese Abfrage durch ist.
 */
export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => api.get<SettingsResponse>('/api/settings'),
    staleTime: 10 * 60_000,
  })
}

/**
 * Die Werte -- und zwar wirklich, nicht "vielleicht schon da".
 *
 * Für das Verkleinern eines Fotos reicht `useSettings()` nicht: wer ein Bild
 * auswählt, während die Abfrage noch unterwegs ist, bekäme die Vorgabe statt
 * der eingestellten Größe. Genau das ist im Test passiert -- und es wäre der
 * unangenehme Fall, weil das Foto dann *aussieht* wie eines, das der
 * Einstellung folgt.
 *
 * Läuft die Abfrage schon oder ist sie frisch, kostet das nichts.
 */
export async function ensureSettings(client: QueryClient): Promise<Settings | null> {
  try {
    const antwort = await client.ensureQueryData({
      queryKey: keys.settings,
      queryFn: () => api.get<SettingsResponse>('/api/settings'),
      staleTime: 10 * 60_000,
    })
    return antwort.settings
  } catch {
    // Ohne Antwort gelten die Vorgaben -- ein Foto soll nicht daran
    // scheitern, dass eine Nebensache nicht durchkam.
    return null
  }
}

export function useSaveSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (werte: Partial<Settings>) =>
      api.patch<SettingsResponse>('/api/settings', werte),
    onSuccess: (antwort) => {
      // Die Antwort trägt den geltenden Stand -- also gekappt, falls jemand
      // über die Decke der `.env` hinaus wollte. Sie zu setzen statt neu zu
      // fragen zeigt genau das an, ohne einen zweiten Weg zum Server.
      client.setQueryData(keys.settings, antwort)
    },
  })
}

export function useThreads() {
  return useQuery({
    queryKey: keys.threads,
    queryFn: () => api.get<Thread[]>('/api/threads'),
    // Bis der Live-Stream aus Etappe 2 da ist, hält ein ruhiges Nachfragen
    // die Chatliste aktuell.
    refetchInterval: 20_000,
  })
}

/**
 * Die Route einer Reise.
 *
 * Lange haltbar und ohne Takt: eine Aufzeichnung kommt schubweise, nicht im
 * Minutentakt -- und sie ist um Größenordnungen schwerer als eine Seite
 * Nachrichten. Wer sie neu will, öffnet die Karte neu.
 */
export function useTracks(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.tracks(tripId),
    queryFn: () => api.get<TracksResponse>(`/api/trips/${tripId}/tracks`),
    enabled,
    staleTime: 5 * 60_000,
  })
}

export function useUploadTrack(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (datei: File) =>
      api.upload<Track>(`/api/trips/${tripId}/tracks`, datei, undefined, {
        'x-datei-name': datei.name,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.tracks(tripId) })
      // **Auch die Highlights.** Der Import liest Aufenthalte aus der
      // Aufzeichnung und legt sie als Vorschläge an -- ohne diese Zeile stünde
      // in der Leiste „1 Vorschlag", und auf der Karte wäre keiner. Genau so
      // im Prüflauf aufgefallen.
      void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
    },
  })
}

/**
 * Eine vorhandene Aufzeichnung noch einmal auf Aufenthalte ansehen.
 *
 * **Eine Datei je Aufruf**, und die Oberfläche geht die Liste durch. Neunzehn
 * Dateien in einer Anfrage wären auf einem geteilten Paket der Bau, der nach
 * der halben Route in die Laufzeitgrenze läuft.
 */
export function useRescan(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (trackId: number) => api.post<RescanResponse>(`/api/tracks/${trackId}/rescan`),
    onSuccess: (antwort) => {
      // Nur wenn etwas entstanden ist -- sonst holt ein Durchgang über zwanzig
      // Dateien zwanzigmal dieselbe Liste.
      if (antwort.suggested > 0) {
        void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
      }
    },
  })
}

export function useDeleteTrack(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/tracks/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.tracks(tripId) })
    },
  })
}

/**
 * Die Highlights einer Reise.
 *
 * Was hier ankommt, hat der Server schon gefiltert: ein Mitlesender bekommt
 * nur Freigegebenes und nur bis zu seinem Stichtag. Die Oberfläche sortiert
 * nicht nach und rechnet nichts nach — sie zeigt, was dasteht.
 */
export function useHighlights(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.highlights(tripId),
    queryFn: () => api.get<HighlightsResponse>(`/api/trips/${tripId}/highlights`),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * Anlegen, ändern, wegnehmen, reagieren, freigeben.
 *
 * Alle über denselben Weg: der Server antwortet mit dem fertigen Highlight,
 * und danach wird die Liste neu geholt. Ein Punkt auf der Karte ist nichts,
 * was sich lohnt, im Zwischenspeicher von Hand nachzupflegen — dafür hängt zu
 * viel daran (Freigabe, Chatbeitrag, Reihenfolge).
 */
export function useHighlightMutations(tripId: number) {
  const client = useQueryClient()
  const frisch = () => {
    void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
    // Eine Freigabe schreibt einen Chatbeitrag -- der gehört sofort in den
    // Verlauf, sonst steht er erst nach dem nächsten Abholen da.
    void client.invalidateQueries({ queryKey: keys.messages(tripId) })
    void client.invalidateQueries({ queryKey: keys.threads })
  }

  const anlegen = useMutation({
    mutationFn: (input: HighlightEingabe) =>
      api.post<Highlight>(`/api/trips/${tripId}/highlights`, input),
    onSuccess: frisch,
  })

  const aendern = useMutation({
    mutationFn: (input: HighlightEingabe & { id: number }) => {
      const { id, ...rest } = input
      return api.patch<Highlight>(`/api/highlights/${id}`, rest)
    },
    onSuccess: frisch,
  })

  const wegnehmen = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/highlights/${id}`),
    onSuccess: frisch,
  })

  const reagieren = useMutation({
    mutationFn: (input: { id: number; emoji: string }) =>
      api.post<Highlight>(`/api/highlights/${input.id}/reactions`, { emoji: input.emoji }),
    onSuccess: frisch,
  })

  const freigeben = useMutation({
    mutationFn: (id: number) => api.post<Highlight>(`/api/highlights/${id}/publish`),
    onSuccess: frisch,
  })

  const zurueckziehen = useMutation({
    mutationFn: (id: number) => api.delete<Highlight>(`/api/highlights/${id}/publish`),
    onSuccess: frisch,
  })

  const alleFreigeben = useMutation({
    mutationFn: (until: string) =>
      api.post<PublishManyResponse>(`/api/trips/${tripId}/highlights/publish`, { until }),
    onSuccess: frisch,
  })

  return { anlegen, aendern, wegnehmen, reagieren, freigeben, zurueckziehen, alleFreigeben }
}

/** Was beim Anlegen und Ändern mitgeht -- alles freiwillig bis auf den Titel. */
export interface HighlightEingabe {
  lat?: number
  lon?: number
  title?: string
  body?: string
  category?: string
  happened_at?: string
  position_source?: Highlight['position_source']
  time_source?: NonNullable<Highlight['time_source']>
  /** Die Uhr an diesem Punkt. Leer mitgeschickt heißt „gilt nicht mehr". */
  tz_offset_min?: number | ''
  show_in_chat?: boolean
  media_ids?: number[]
  /**
   * Was die Vorschau geholt hat — Ortsname, der rohe Block der Ortssuche, das
   * Wetter zur Stunde.
   *
   * Nur mitschicken, wenn wirklich frisch geholt: sie **überschreiben**, was am
   * Highlight steht, und der Server stempelt `enriched_at` dazu. Der Server
   * prüft die Form beim Annehmen; erfundene Felder fallen weg.
   */
  place_name?: string
  place?: Record<string, unknown> | null
  weather?: Record<string, unknown> | null
}

/**
 * Ort und Wetter nachholen — eines nach dem anderen.
 *
 * **Warum die Oberfläche das treibt und nicht der Server beim Import:** ein
 * Import mit fünfundzwanzig Aufenthalten wären fünfzig Abrufe nach außen in
 * einer einzigen Anfrage. Auf einem geteilten Paket mit begrenzter Laufzeit
 * ist das der Bau, der nach einem langen Reisetag abbricht und die halbe
 * Route eingespielt zurücklässt.
 *
 * Also hier, nacheinander, während die Kartenseite offen ist. Jedes Highlight
 * wird **einmal** geholt; der Server merkt sich das (`enriched_at`) auch dann,
 * wenn nichts zurückkam.
 */
export function useAngaben(tripId: number, highlights: Highlight[], erlaubt: boolean) {
  const client = useQueryClient()
  const laeuft = useRef(false)
  const [offen, setOffen] = useState(0)

  const fehlend = highlights.filter((h) => h.enriched_at === null).length

  useEffect(() => {
    if (!erlaubt || laeuft.current) return
    const ids = highlights.filter((h) => h.enriched_at === null).map((h) => h.id)
    if (ids.length === 0) return

    laeuft.current = true
    setOffen(ids.length)
    let abgebrochen = false

    void (async () => {
      for (const id of ids) {
        if (abgebrochen) break
        try {
          await api.post(`/api/highlights/${id}/enrich`)
        } catch (fehler) {
          // Nicht eingerichtet oder kein Netz: dann bringt auch der nächste
          // nichts. Ein einzelner Fehlschlag am Dienst hält den Lauf dagegen
          // nicht auf — der Server merkt sich ihn und geht weiter.
          if (fehler instanceof ApiError && (fehler.offline || fehler.status === 409)) break
        }
        setOffen((n) => Math.max(0, n - 1))
      }

      laeuft.current = false
      setOffen(0)
      // Erst am Ende neu laden: mittendrin käme eine neue Liste herein und
      // der Lauf finge von vorn an.
      if (!abgebrochen) void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
    })()

    return () => {
      abgebrochen = true
    }
    // An der **Zahl** der offenen Einträge, nicht an der Liste: die ist bei
    // jedem Abruf ein neues Feld, und der Effekt liefe bei jedem Rendern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, erlaubt, fehlend])

  return offen
}

/**
 * Die zwei Vorschläge: Ort → Zeit und Zeit → Ort.
 *
 * Beide sind Vorschläge und nichts weiter. Die Route ist beim Import
 * ausgedünnt worden, und zwischen zwei behaltenen Punkten liegt eine gerade
 * Linie, auf der niemand war — deshalb kommt der Abstand mit heraus und die
 * Oberfläche lässt ablehnen.
 */
/**
 * Ort und Wetter zu einem Punkt ansehen, bevor etwas gespeichert ist.
 *
 * Der Weg über den Server ist der Punkt: nach außen geht so seine Adresse und
 * nicht die des Geräts. Gespeichert wird dabei nichts — was das Formular zeigt,
 * ist eine Auskunft, kein Eintrag. Erst das gesicherte Highlight holt sie sich
 * über `enrich` noch einmal und behält sie.
 */
export function useLookup(tripId: number) {
  return useMutation({
    mutationFn: (input: { lat: number; lon: number; at?: string }) =>
      api.post<LookupResponse>(`/api/trips/${tripId}/lookup`, input),
  })
}

/**
 * Was rund um den Punkt liegt — Lokale, Museen, Strände.
 *
 * **Geschickt wird eine Marke, kein Suchwort.** Aus `essen` macht der Server
 * „Restaurant" und eine Handvoll Apple-Kategorien (`server/src/Umgebung.php`);
 * hier steht davon nichts. Das ist nicht nur Ordnung: ein frei eingetipptes
 * Wort wäre die Suchanfrage eines Menschen bei einem fremden Dienst, und das
 * ist etwas anderes als eine von sieben Schubladen.
 *
 * Und wie bei `useLookup` geht die Anfrage über den eigenen Server — nach außen
 * geht seine Adresse, nicht die des Geräts.
 */
export function useUmgebung(tripId: number) {
  return useMutation({
    mutationFn: (input: { kind: string; lat: number; lon: number }) =>
      api.post<UmgebungResponse>(`/api/trips/${tripId}/umgebung`, input),
  })
}

export function useVorschlag(tripId: number) {
  const aufDieRoute = useMutation({
    mutationFn: (input: { lat: number; lon: number }) =>
      api.post<SnapResponse>(`/api/trips/${tripId}/snap`, input),
  })
  const zurZeit = useMutation({
    mutationFn: (at: string) => api.post<LocateResponse>(`/api/trips/${tripId}/locate`, { at }),
  })
  return { aufDieRoute, zurZeit }
}

export function useTrip(tripId: number) {
  return useQuery({
    queryKey: keys.trip(tripId),
    queryFn: () => api.get<Trip>(`/api/trips/${tripId}`),
  })
}

export function useMembers(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.members(tripId),
    queryFn: () => api.get<Member[]>(`/api/trips/${tripId}/members`),
    enabled,
  })
}

export function useInvitations(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.invitations(tripId),
    queryFn: () => api.get<Invitation[]>(`/api/trips/${tripId}/invitations`),
    enabled,
  })
}

export function useInvitationPreview(code: string) {
  return useQuery({
    queryKey: ['invitation', code],
    queryFn: () => api.get<InvitationPreview>(`/api/invitations/${code}`),
    retry: false,
  })
}

/**
 * Der Verlauf eines Chats.
 *
 * Geladen wird seitenweise von neu nach alt; die Anzeige dreht das um. Statt
 * `useInfiniteQuery` wird hier bewusst eine flache Liste im Cache gehalten:
 * neue Nachrichten kommen vorne dazu, ältere hinten -- das ist mit einer
 * einzigen Liste einfacher zu denken als mit Seiten, die sich überlappen.
 */
export function useMessages(tripId: number, withUser?: number) {
  return useQuery({
    queryKey: keys.messages(tripId, withUser),
    queryFn: () =>
      api.get<MessagePage>(`/api/trips/${tripId}/messages`, {
        with: withUser,
      }),
    refetchInterval: 15_000,
  })
}

export function useOlderMessages(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (before: number) =>
      api.get<MessagePage>(`/api/trips/${tripId}/messages`, {
        with: withUser,
        before,
      }),
    onSuccess: (page) => {
      client.setQueryData<MessagePage>(keys.messages(tripId, withUser), (old) =>
        old
          ? { messages: [...old.messages, ...page.messages], next_before: page.next_before }
          : page,
      )
    },
  })
}

/**
 * Lädt ein verkleinertes Foto hoch, noch bevor der Beitrag abgeschickt ist.
 *
 * Aufnahmeort und -zeit gehen als Kopfzeilen mit — der Server legt sie an der
 * Datei ab, und die Karte baut daraus später einen Vorschlag, ohne das Bild
 * noch einmal zu lesen.
 */
export function useUploadMedia(tripId: number) {
  return useMutation({
    mutationFn: (input: {
      blob: Blob
      aufnahme?: Aufnahme
      onProgress?: (anteil: number) => void
    }) =>
      api.upload<Media>(
        `/api/trips/${tripId}/media`,
        input.blob,
        input.onProgress,
        aufnahmeKopf(input.aufnahme),
      ),
  })
}

/**
 * Die eigenen Bilder dieser Reise — für die Auswahl „Aus dieser Reise".
 *
 * Nur wenn die Auswahl offen ist: eine Reise kann Hunderte Bilder haben, und
 * niemand braucht die Liste, solange er nur ein Highlight ansieht.
 */
export function useReisefotos(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.reisefotos(tripId),
    queryFn: () => api.get<Reisefotos>(`/api/trips/${tripId}/media`),
    enabled,
    staleTime: 30_000,
  })
}

/** Wirft ein hochgeladenes Foto weg, das doch nicht mitsoll. */
export function useDiscardMedia() {
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/media/${id}`),
  })
}

export function useSendMessage(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      body: string
      mediaIds?: number[]
      clientKey?: string
      /** Worauf sich das bezieht -- der Server prüft es gegen die Regel. */
      replyToId?: number
      replyToMediaId?: number
    }) =>
      api.post<Message>(
        `/api/trips/${tripId}/messages`,
        {
          body: input.body,
          media_ids: input.mediaIds ?? [],
          // Damit dieselbe Nachricht nicht zweimal im Bericht steht, wenn der
          // Versuch scheiterte, ohne dass er scheiterte -- eine verlorene
          // Antwort sieht wie ein Fehlschlag aus, ist aber keiner.
          client_key: input.clientKey,
          reply_to_id: input.replyToId,
          reply_to_media_id: input.replyToMediaId,
        },
        // Im Antwortverlauf schreibt der Reisende an genau diesen einen
        // Menschen. Dass daraus eine private Nachricht wird, entscheidet der
        // Server -- hier steht nur, welcher Verlauf offen ist.
        { with: withUser },
      ),
    onSuccess: (message) => {
      client.setQueryData<MessagePage>(keys.messages(tripId, withUser), (old) =>
        old
          ? { ...old, messages: [message, ...old.messages] }
          : { messages: [message], next_before: null },
      )
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

export function useEditMessage(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; body: string }) =>
      api.patch<Message>(`/api/messages/${input.id}`, { body: input.body }),
    onSuccess: (message) => {
      // Die geänderte Nachricht an ihrem Platz ersetzen, statt den ganzen
      // Verlauf neu zu laden -- sonst springt die Ansicht beim Absenden.
      client.setQueryData<MessagePage>(keys.messages(tripId, withUser), (old) =>
        old
          ? {
              ...old,
              messages: old.messages.map((m) =>
                // Der Haken bleibt, wie er war. Wer den Text ändert, ändert
                // nicht, wer ihn schon gesehen hat -- und die Antwort auf ein
                // PATCH weiß nichts vom geöffneten Verlauf, könnte ihn also
                // nur raten. Lieber stehenlassen als falsch neu setzen.
                m.id === message.id ? { ...message, read: m.read } : m,
              ),
            }
          : old,
      )
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Setzt, ersetzt oder nimmt eine Reaktion zurück.
 *
 * Dasselbe Emoji noch einmal nimmt es zurück, ein anderes ersetzt es -- eine
 * Person hat je Ziel genau eine Reaktion. Der Server schickt den neuen Stand
 * der ganzen Nachricht zurück; hier wird nur diese eine ersetzt, damit der
 * Verlauf nicht springt.
 */
export function useToggleReaction(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { messageId: number; emoji: string; mediaId?: number }) =>
      api.post<{ message_id: number; reactions: Reaction[] }>(
        `/api/messages/${input.messageId}/reactions`,
        { emoji: input.emoji, media_id: input.mediaId ?? 0 },
      ),
    onSuccess: ({ message_id, reactions }) => {
      client.setQueryData<MessagePage>(keys.messages(tripId, withUser), (old) =>
        old
          ? {
              ...old,
              messages: old.messages.map((m) =>
                m.id === message_id ? { ...m, reactions } : m,
              ),
            }
          : old,
      )
    },
  })
}

export function useDeleteMessage(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/messages/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.messages(tripId, withUser) })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

export function useMarkRead(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<void>(`/api/trips/${tripId}/read`, {}, { with: withUser }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/** Die Kontenliste -- der Server gibt sie nur der Verwaltung. */
export function usePeople(enabled: boolean) {
  return useQuery({
    queryKey: keys.people,
    queryFn: () => api.get<Person[]>('/api/people'),
    enabled,
  })
}

/** Entfernt jemanden aus einer Reise -- nur der Absender darf das. */
export function useRemoveMember(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) =>
      api.delete<void>(`/api/trips/${tripId}/members/${userId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
      void client.invalidateQueries({ queryKey: keys.trip(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/** Legt ein Konto still oder gibt es wieder frei -- nur die Verwaltung. */
/**
 * Löscht ein Konto endgültig -- nur für die, an denen nichts hängt.
 *
 * Der Server prüft das noch einmal selbst; hier wird der Knopf gar nicht
 * erst angeboten, wo er scheitern würde.
 */
export function useDeletePerson() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/people/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.people })
    },
  })
}

export function useSetDisabled() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; disabled: boolean }) =>
      api.post<{ id: number; disabled: boolean }>(`/api/people/${input.id}/disabled`, {
        disabled: input.disabled,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.people })
    },
  })
}

export function useSetRole() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; role: Person['role'] }) =>
      api.post<{ id: number; role: Person['role'] }>(`/api/people/${input.id}/role`, {
        role: input.role,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.people })
    },
  })
}

export function useCreateTrip() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      title: string
      description: string
      starts_on?: string
      ends_on?: string
    }) => api.post<Trip>('/api/trips', input),
    onSuccess: () => invalidateAll(client),
  })
}

/**
 * Der Zustand einer Reise -- drei Stufen, eine Leiter.
 *
 * `active` läuft, `closed` beendet das Schreiben für alle, `private` nimmt
 * sie zusätzlich aus den Listen der Mitlesenden. Gelöscht wird in keinem
 * Fall etwas.
 */
/**
 * Titel, Beschreibung und Reisezeitraum ändern.
 *
 * Geschickt wird nur, was sich geändert hat: der Server lässt stehen, was
 * fehlt, und löscht, was leer kommt.
 */
export function useUpdateTrip(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (
      input: Partial<Pick<Trip, 'title' | 'description' | 'starts_on' | 'ends_on'>> & {
        /** Leer mitgeschickt löscht die Uhr der Reise — wie beim Datum. */
        tz_offset_min?: number | ''
      },
    ) => api.patch<Trip>(`/api/trips/${tripId}`, input),
    onSuccess: (trip) => {
      client.setQueryData(keys.trip(tripId), trip)
      void client.invalidateQueries({ queryKey: keys.trips })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

export function useSetTripState(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (state: TripState) =>
      api.post<Trip>(`/api/trips/${tripId}/state`, { state }),
    onSuccess: (trip) => {
      client.setQueryData(keys.trip(tripId), trip)
      void invalidateAll(client)
    },
  })
}

export function useSetMuted(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (muted: boolean) =>
      api.post<{ muted: boolean }>(`/api/trips/${tripId}/mute`, { muted }),
    onSuccess: ({ muted }) => {
      client.setQueryData<Trip>(keys.trip(tripId), (old) =>
        old ? { ...old, muted } : old,
      )
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Zieht einen Einladungslink zurück.
 *
 * Der Code bleibt in der Datenbank stehen (`revoked_at`), verschwindet aber
 * aus der Liste und führt nirgends mehr hin. Rückgängig macht das niemand --
 * deshalb fragt die Oberfläche vorher.
 */
export function useRevokeInvitation(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.delete<void>(`/api/trips/${tripId}/invitations/${code}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.invitations(tripId) })
    },
  })
}

export function useCreateInvitation(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (maxUses?: number) =>
      api.post<Invitation>(`/api/trips/${tripId}/invitations`, { max_uses: maxUses }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.invitations(tripId) })
    },
  })
}

export function useAcceptInvitation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ state: 'joined' | 'pending'; trip: Trip | null }>(
        `/api/invitations/${code}/accept`,
      ),
    onSuccess: () => invalidateAll(client),
  })
}

/**
 * Wo man mit dieser Einladung steht.
 *
 * Gebraucht, seit zwischen dem Klick auf den Link und dem Mitlesen eine
 * Bestätigung liegen kann. Nach dem Anlegen des Kontos wüsste die
 * Einladungsseite sonst nicht, ob sie in die Reise führen oder „wartet auf
 * Bestätigung" zeigen soll.
 */
export function useInvitationStatus(code: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.invitationStatus(code),
    queryFn: () => api.get<InvitationStatus>(`/api/invitations/${code}/status`),
    enabled,
    retry: false,
  })
}

export function useJoinRequests(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.requests(tripId),
    queryFn: () => api.get<JoinRequest[]>(`/api/trips/${tripId}/requests`),
    enabled,
  })
}

export function useDecideRequest(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { userId: number; approve: boolean; disableAccount?: boolean }) =>
      api.post<void>(`/api/trips/${tripId}/requests/${input.userId}`, {
        approve: input.approve,
        disable_account: input.disableAccount ?? false,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.requests(tripId) })
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
      void client.invalidateQueries({ queryKey: keys.people })
      void client.invalidateQueries({ queryKey: keys.trip(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Jemanden aus dem Verteiler nehmen -- oder wieder hinein.
 *
 * Der leise Bruder von `useRemoveMember`: die Reise bleibt in seiner Liste,
 * nur kommt nichts Neues mehr dazu.
 */
export function useSetCutoff(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { userId: number; cut: boolean }) =>
      api.post<{ user_id: number; cut_off: boolean }>(
        `/api/trips/${tripId}/members/${input.userId}/cutoff`,
        { cut: input.cut },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
    },
  })
}

export function useSetApproval(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (required: boolean) =>
      api.post<Trip>(`/api/trips/${tripId}/approval`, { required }),
    onSuccess: (trip) => {
      client.setQueryData(keys.trip(tripId), trip)
      void client.invalidateQueries({ queryKey: keys.trips })
    },
  })
}

export function useLogin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<Me>('/api/auth/login', input),
    onSuccess: (me) => {
      client.setQueryData(keys.me, me)
      void invalidateAll(client)
    },
  })
}

export function useRegister() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      email: string
      password: string
      display_name: string
      invite_code?: string
    }) => api.post<Me>('/api/auth/register', input),
    onSuccess: (me) => {
      client.setQueryData(keys.me, me)
      void invalidateAll(client)
    },
  })
}

/**
 * Fordert einen Link zum Zurücksetzen an.
 *
 * Der Server antwortet immer gleich, auch wenn es die Adresse nicht gibt --
 * die Oberfläche darf hier also nichts unterscheiden, was er verschweigt.
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => api.post<void>('/api/auth/forgot', { email }),
  })
}

export function useResetPassword() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      api.post<Me>('/api/auth/reset', input),
    onSuccess: (me) => {
      // Das Zurücksetzen meldet gleich an; alle bisherigen Sitzungen hat der
      // Server verworfen.
      client.setQueryData(keys.me, me)
      void invalidateAll(client)
    },
  })
}

export function useLogout() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/api/auth/logout'),
    onSuccess: () => {
      // Alles verwerfen: nach dem Abmelden darf nichts aus dem Cache mehr
      // durchscheinen. Dazu gehört auch der zuletzt offene Chat -- wer sich
      // als Nächstes an diesem Gerät anmeldet, hat mit ihm nichts zu tun.
      client.clear()
      vergissOrt()
    },
  })
}

function invalidateAll(client: QueryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: keys.threads }),
    client.invalidateQueries({ queryKey: keys.trips }),
  ])
}
